<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useInbox } from 'dashboard/composables/useInbox';
import ProductTemplatesAPI from 'dashboard/api/productTemplates';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const { t } = useI18n();
const { isAWhatsAppCloudChannel } = useInbox();

const isLoading = ref(false);
const sendingTemplateId = ref(null);
const query = ref('');
const templates = ref([]);

const filteredTemplates = computed(() => {
  const activeTemplates = templates.value.filter(template => template.active);
  const normalizedQuery = query.value.trim().toLowerCase();

  if (!normalizedQuery) {
    return activeTemplates;
  }

  return activeTemplates.filter(template => {
    const searchableText = [
      template.name,
      template.template_key,
      template.product_name,
      template.product_sku,
    ]
      .join(' ')
      .toLowerCase();

    return searchableText.includes(normalizedQuery);
  });
});

const productDisplayName = template => template.product_name || template.name;

const productPriceLabel = template => {
  if (!template.product_price && !template.product_currency) {
    return '-';
  }

  return [template.product_price, template.product_currency]
    .filter(Boolean)
    .join(' ');
};

const productSendMode = template => {
  const hasCatalogIdentifiers =
    template.catalog_id && template.product_retailer_id;

  if (isAWhatsAppCloudChannel.value && hasCatalogIdentifiers) {
    return t('CONVERSATION_SIDEBAR.PRODUCT_CATALOG.MODE_WHATSAPP_CATALOG');
  }

  return t('CONVERSATION_SIDEBAR.PRODUCT_CATALOG.MODE_FALLBACK');
};

const fetchTemplates = async () => {
  isLoading.value = true;

  try {
    const response = await ProductTemplatesAPI.get();
    templates.value = response.data;
  } catch {
    useAlert(t('CONVERSATION_SIDEBAR.PRODUCT_CATALOG.ERROR_FETCH'));
  } finally {
    isLoading.value = false;
  }
};

const sendProduct = async template => {
  sendingTemplateId.value = template.id;

  try {
    await ProductTemplatesAPI.sendProduct(template.id, {
      conversation_id: props.conversationId,
    });

    useAlert(
      t('CONVERSATION_SIDEBAR.PRODUCT_CATALOG.SEND_SUCCESS', {
        name: productDisplayName(template),
      })
    );
  } catch (error) {
    const responseMessage =
      error?.response?.data?.message || error?.response?.data?.error;
    const message =
      (Array.isArray(responseMessage) && responseMessage.join(', ')) ||
      (typeof responseMessage === 'string' && responseMessage) ||
      t('CONVERSATION_SIDEBAR.PRODUCT_CATALOG.ERROR_SEND');
    useAlert(message);
  } finally {
    sendingTemplateId.value = null;
  }
};

onMounted(fetchTemplates);
</script>

<template>
  <div class="px-4 py-3">
    <p class="mb-3 text-xs text-n-slate-10">
      {{ $t('CONVERSATION_SIDEBAR.PRODUCT_CATALOG.DESCRIPTION') }}
    </p>

    <input
      v-model="query"
      type="text"
      class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
      :placeholder="$t('CONVERSATION_SIDEBAR.PRODUCT_CATALOG.SEARCH')"
    />

    <div v-if="isLoading" class="flex items-center justify-center py-8">
      <Spinner size="24" class="text-n-brand" />
    </div>

    <div
      v-else-if="!filteredTemplates.length"
      class="py-6 text-sm text-center text-n-slate-11"
    >
      {{ $t('CONVERSATION_SIDEBAR.PRODUCT_CATALOG.EMPTY') }}
    </div>

    <div v-else class="mt-3 space-y-3">
      <div
        v-for="template in filteredTemplates"
        :key="template.id"
        class="rounded-xl border border-n-weak bg-n-solid-1 p-3"
      >
        <div class="flex items-start gap-3">
          <img
            v-if="template.product_image_url"
            :src="template.product_image_url"
            :alt="productDisplayName(template)"
            class="h-14 w-14 rounded-lg border border-n-weak object-cover"
          />

          <div class="min-w-0 flex-1">
            <p class="truncate text-sm font-medium text-n-slate-12">
              {{ productDisplayName(template) }}
            </p>
            <p class="truncate text-xs text-n-slate-10">
              {{ template.description || template.template_key }}
            </p>
            <p class="mt-1 text-xs text-n-slate-11">
              {{
                $t('CONVERSATION_SIDEBAR.PRODUCT_CATALOG.SKU', {
                  sku: template.product_sku || '-',
                })
              }}
            </p>
            <p class="text-xs text-n-slate-11">
              {{
                $t('CONVERSATION_SIDEBAR.PRODUCT_CATALOG.PRICE', {
                  price: productPriceLabel(template),
                })
              }}
            </p>
            <p
              class="mt-1 text-[11px] font-medium uppercase tracking-wide text-n-brand"
            >
              {{ productSendMode(template) }}
            </p>
          </div>
        </div>

        <Button
          class="mt-3"
          sm
          :label="$t('CONVERSATION_SIDEBAR.PRODUCT_CATALOG.SEND')"
          :is-loading="sendingTemplateId === template.id"
          @click="sendProduct(template)"
        />
      </div>
    </div>
  </div>
</template>
